import type { FC } from "react";
import { type ArtPoint, AssistantAvatarPointSide } from "../../assistantAvatarGeometry";
import { AVATAR_PART } from "../../assistantAvatarMotion";

/** A left-side arm on the 120-unit canvas; the right arm is its mirror around the centre line. */
export interface PointingArmGeometry {
	/** Where the arm leaves the figure — hidden behind the body or blended into the shoulder. */
	shoulder: ArtPoint;
	/** Quadratic control point that bends the arm upward. */
	bend: ArtPoint;
	hand: ArtPoint;
	handRadius: number;
	armWidth: number;
}

interface AvatarPointingHandProps {
	side: AssistantAvatarPointSide;
	geometry: PointingArmGeometry;
	armColor: string;
	/** Plain hand: the body fill on a character, the skin tone on a human. */
	handFill: string;
	outline: string;
}

const CANVAS_CENTER_X = 60;
const mirror = (point: ArtPoint): ArtPoint => ({ x: 2 * CANVAS_CENTER_X - point.x, y: point.y });

const PART_BY_SIDE: Record<AssistantAvatarPointSide, string> = {
	[AssistantAvatarPointSide.Left]: AVATAR_PART.POINT_LEFT,
	[AssistantAvatarPointSide.Right]: AVATAR_PART.POINT_RIGHT,
};

/** An arm raised toward the tour target; the root motion nudges it toward that side twice, then it rests. */
export const AvatarPointingHand: FC<AvatarPointingHandProps> = ({ side, geometry, armColor, handFill, outline }) => {
	const place = side === AssistantAvatarPointSide.Left ? (point: ArtPoint) => point : mirror;
	const shoulder = place(geometry.shoulder);
	const bend = place(geometry.bend);
	const hand = place(geometry.hand);
	return (
		<g className={PART_BY_SIDE[side]} data-point-side={side}>
			<path
				d={`M${shoulder.x} ${shoulder.y} Q${bend.x} ${bend.y} ${hand.x} ${hand.y}`}
				fill="none"
				stroke={armColor}
				strokeWidth={geometry.armWidth}
				strokeLinecap="round"
			/>
			<circle cx={hand.x} cy={hand.y} r={geometry.handRadius} fill={handFill} stroke={outline} />
		</g>
	);
};
